import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';

export interface ConfirmOptions {
  readonly title?: string;
  readonly description: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly variant?: 'default' | 'destructive';
}

interface ConfirmContextValue {
  readonly confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

interface PendingConfirm extends ConfirmOptions {
  readonly resolve: (v: boolean) => void;
}

// Both component and hook live here so the imperative `useConfirm()` API stays
// colocated with its provider. react-refresh expects component-only files; the
// hook export is intentional here.
/* eslint-disable react-refresh/only-export-components */

export function ConfirmDialogProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const close = (result: boolean): void => {
    if (pending) {
      pending.resolve(result);
      setPending(null);
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) close(false);
        }}
      >
        {pending ? (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{pending.title ?? t('common.confirmTitle')}</DialogTitle>
              <DialogDescription>{pending.description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  close(false);
                }}
              >
                {pending.cancelLabel ?? t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant={pending.variant === 'destructive' ? 'destructive' : 'default'}
                onClick={() => {
                  close(true);
                }}
              >
                {pending.confirmLabel ?? t('common.confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within <ConfirmDialogProvider>');
  }
  return ctx.confirm;
}

/* eslint-enable react-refresh/only-export-components */
